import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, NumberInput, ToggleGroup, ToggleGroupItem, Button } from "@tellescope/ui";
import type { EnduserObservation } from "@tellescope/types-models";
import { displayWeightToLb, lbToDisplayWeight, type WeightDisplayUnit } from "../../../utils/nutritionUnitConversions";

interface WeightEntryFieldProps {
  latestWeight: EnduserObservation | undefined;
  /** When set, the form pre-fills from this entry and submits via onUpdate instead of onLog. */
  editingEntry?: EnduserObservation;
  onLog: (valueInLb: number) => Promise<void>;
  onUpdate?: (valueInLb: number) => Promise<void>;
  isPending: boolean;
}

export const WeightEntryField = ({ latestWeight, editingEntry, onLog, onUpdate, isPending }: WeightEntryFieldProps) => {
  const { t } = useTranslation("portal");
  const [displayUnit, setDisplayUnit] = useState<WeightDisplayUnit>("lb");
  const [value, setValue] = useState<number | undefined>(
    editingEntry ? lbToDisplayWeight(editingEntry.measurement.value, "lb") : undefined
  );

  const handleUnitChange = (unit: string) => {
    if (unit === "lb" || unit === "kg") {
      setDisplayUnit(unit);
    }
  };

  const handleSubmit = async () => {
    if (value === undefined || value <= 0) return;
    const valueInLb = displayWeightToLb(value, displayUnit);
    if (editingEntry) {
      await onUpdate?.(valueInLb);
    } else {
      await onLog(valueInLb);
      setValue(undefined);
    }
  };

  return (
    <div className="space-y-3">
      {latestWeight && !editingEntry && (
        <p className="text-xs text-muted-foreground">
          {t("Last logged: {{value}} {{unit}}", {
            value: lbToDisplayWeight(latestWeight.measurement.value, displayUnit).toFixed(1),
            unit: displayUnit,
          })}
        </p>
      )}
      <div className="flex items-end gap-3">
        <div className="flex-1 space-y-1.5">
          <Label>{t("Weight")}</Label>
          <NumberInput
            value={value}
            onChange={setValue}
            precisionOptions={{ maxDecimalDigitCount: 1 }}
            placeholder="0.0"
            data-test="weight-input"
          />
        </div>
        <ToggleGroup type="single" value={displayUnit} onValueChange={handleUnitChange}>
          <ToggleGroupItem value="lb">{t("lb")}</ToggleGroupItem>
          <ToggleGroupItem value="kg">{t("kg")}</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <Button
        onClick={handleSubmit}
        disabled={value === undefined || value <= 0 || isPending}
        data-test="log-weight-button"
      >
        {editingEntry ? t("Update reading") : t("Save reading")}
      </Button>
    </div>
  );
};
